import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { listTemplates, saveCustomTemplate, deleteCustomTemplate, toggleTemplateFavorite, launchTemplate } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Star, RocketLaunch, Copy, Trash, Eye, MagnifyingGlass, Sparkle, UsersThree,
  CurrencyDollar, Megaphone, ShareNetwork, ArrowRight, GlobeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { MarketingResources } from "@/components/MarketingResources";

const CHANNEL_COLOR = {
  Facebook: "text-sky-400 bg-sky-500/10", Instagram: "text-pink-400 bg-pink-500/10",
  Google: "text-emerald-400 bg-emerald-500/10", Email: "text-amber-300 bg-amber-500/10",
  SMS: "text-violet-300 bg-violet-500/10",
};

function TemplateCard({ t, onPreview, onLaunch, onDuplicate, onFav, onDelete }) {
  return (
    <div className="card-matte p-4 flex flex-col" data-testid={`template-card-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{t.name}</div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${CHANNEL_COLOR[t.channel] || "text-[#9a9a9a] bg-white/6"}`}>{t.channel || "Multi"}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/6 text-[#9a9a9a]">{t.category}</span>
            {!t.system && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#6B3DFF]/12 text-[#C39BFF]">Custom{t.shared ? " · Shared" : ""}</span>}
          </div>
        </div>
        <button data-testid={`template-fav-${t.id}`} onClick={() => onFav(t)} className="shrink-0">
          <Star size={16} weight={t.favorite ? "fill" : "regular"} className={t.favorite ? "text-amber-400" : "text-[#5a5a5a] hover:text-amber-400"} />
        </button>
      </div>
      <div className="text-xs text-[#9a9a9a] mt-2 line-clamp-3 flex-1">{t.caption}</div>
      {t.hashtags && <div className="text-[10px] text-sky-400 mt-1.5 line-clamp-1">{t.hashtags}</div>}
      <div className="flex items-center gap-3 mt-2 text-[10px] text-[#7a7a7a] flex-wrap">
        {t.audience && <span className="flex items-center gap-1"><UsersThree size={11} /> {t.audience}</span>}
        {t.budget && <span className="flex items-center gap-1"><CurrencyDollar size={11} /> {t.budget}</span>}
      </div>
      <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-white/8">
        <button data-testid={`template-preview-${t.id}`} onClick={() => onPreview(t)}
          className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[#cfcfcf]"><Eye size={12} /> Preview</button>
        <button data-testid={`template-launch-${t.id}`} onClick={() => onLaunch(t)}
          className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-[#6B3DFF]/15 hover:bg-[#6B3DFF]/25 text-[#C39BFF] border border-[#6B3DFF]/25"><RocketLaunch size={12} weight="fill" /> Launch</button>
        <button data-testid={`template-duplicate-${t.id}`} onClick={() => onDuplicate(t)}
          className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[#9a9a9a]"><Copy size={12} /></button>
        {!t.system && t.own && (
          <button data-testid={`template-delete-${t.id}`} onClick={() => onDelete(t)} className="ml-auto text-[#5a5a5a] hover:text-red-400"><Trash size={13} /></button>
        )}
      </div>
    </div>
  );
}

export default function TemplatesPage() {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [preview, setPreview] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [edit, setEdit] = useState({ caption: "", hashtags: "", cta: "", personalize: true });
  const [custom, setCustom] = useState(null);
  const [view, setView] = useState("templates");

  const key = ["templates", activeId, category, q, favOnly];
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => listTemplates(activeId, { ...(category !== "all" ? { category } : {}), ...(q ? { q } : {}), ...(favOnly ? { favorites: true } : {}) }),
    enabled: !!activeId,
  });
  const templates = data?.templates || [];
  const categories = data?.categories || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["templates", activeId] });

  const fav = useMutation({ mutationFn: (t) => toggleTemplateFavorite(activeId, t.id), onSuccess: refresh });
  const del = useMutation({ mutationFn: (t) => deleteCustomTemplate(t.id), onSuccess: () => { toast.success("Template deleted"); refresh(); } });
  const launchM = useMutation({
    mutationFn: () => launchTemplate({ client_id: activeId, template_id: launch.id, personalize: edit.personalize,
      caption: edit.caption, hashtags: edit.hashtags, cta: edit.cta }),
    onSuccess: (r) => {
      setLaunch(null);
      toast.success(r.personalized ? "Campaign personalized to your brand & queued for approval 🚀" : "Campaign queued for approval 🚀",
        { action: { label: "Open Queue", onClick: () => navigate("/approvals") } });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Launch failed"),
  });
  const saveCustom = useMutation({
    mutationFn: () => saveCustomTemplate({ client_id: activeId, ...custom }),
    onSuccess: () => { setCustom(null); toast.success("Saved as custom template"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const openLaunch = (t) => { setLaunch(t); setEdit({ caption: t.caption || "", hashtags: t.hashtags || "", cta: t.cta || "", personalize: true }); };
  const openDuplicate = (t) => setCustom({
    name: `${t.name} (Copy)`, category: t.category, channel: t.channel || "Facebook", prompt: t.prompt || "",
    image_style: t.image_style || "", caption: t.caption || "", hashtags: t.hashtags || "", cta: t.cta || "",
    audience: t.audience || "", budget: t.budget || "", shared: false,
  });

  return (
    <PageContainer>
      <PageHeader eyebrow="Agency" title="Templates & Resources"
        subtitle={`Launch complete marketing campaigns for ${activeClient?.name || "your brand"} in one click — plus a searchable vault of ready-to-use marketing copy for every channel.`} />

      <div className="flex items-center gap-2 mb-5" data-testid="templates-view-tabs">
        {[["templates", "Campaign Templates"], ["resources", "Marketing Resources"]].map(([k, l]) => (
          <button key={k} data-testid={`templates-view-${k}`} onClick={() => setView(k)}
            className={`h-9 rounded-full px-4 text-sm font-semibold border transition-colors ${view === k ? "bg-[#6B3DFF] border-[#6B3DFF] text-white" : "bg-white/5 border-white/10 text-[#9a9a9a] hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {view === "resources" && <MarketingResources />}

      {view === "templates" && (<>
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5a5a5a]" />
          <Input data-testid="tpl-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…"
            className="bg-[#10101A] border-white/10 pl-8 h-8 w-52 text-xs" />
        </div>
        <button data-testid="tpl-fav-filter" onClick={() => setFavOnly((v) => !v)}
          className={`text-xs px-2.5 py-1.5 rounded-lg border flex items-center gap-1 ${favOnly ? "border-amber-400/40 bg-amber-500/10 text-amber-300" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>
          <Star size={13} weight={favOnly ? "fill" : "regular"} /> Favorites
        </button>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-5" data-testid="tpl-categories">
        <button data-testid="tpl-cat-all" onClick={() => setCategory("all")}
          className={`text-xs px-2.5 py-1.5 rounded-full border ${category === "all" ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>All</button>
        {categories.map((c) => (
          <button key={c} data-testid={`tpl-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setCategory(c)}
            className={`text-xs px-2.5 py-1.5 rounded-full border ${category === c ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/10 text-white" : "border-white/12 text-[#9a9a9a] hover:text-white"}`}>{c}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="tpl-grid">
        {templates.map((t) => (
          <TemplateCard key={t.id} t={t} onPreview={setPreview} onLaunch={openLaunch}
            onDuplicate={openDuplicate} onFav={(x) => fav.mutate(x)} onDelete={(x) => del.mutate(x)} />
        ))}
      </div>
      {!templates.length && <div className="text-center text-sm text-[#7a7a7a] py-10" data-testid="tpl-empty">No templates match — clear the filters or save your own.</div>}
      </>)}

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tpl-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{preview?.name}</DialogTitle>
            <DialogDescription className="text-[#7a7a7a] text-xs">{preview?.category} · {preview?.channel}</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              {[["Caption", preview.caption], ["Hashtags", preview.hashtags], ["Call to Action", preview.cta],
                ["AI Image Prompt", preview.prompt], ["Image Style", preview.image_style],
                ["Suggested Audience", preview.audience], ["Recommended Budget", preview.budget]].map(([l, v]) => v ? (
                <div key={l}>
                  <div className="label-caps text-[10px] mb-1">{l}</div>
                  <div className="text-xs text-[#cfcfcf] bg-[#10101A] border border-white/8 rounded-lg p-2.5 whitespace-pre-wrap">{v}</div>
                </div>
              ) : null)}
              <div className="flex justify-end gap-2 pt-1">
                <Button data-testid="tpl-preview-launch" onClick={() => { const t = preview; setPreview(null); openLaunch(t); }}
                  className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><RocketLaunch size={14} className="mr-1.5" weight="fill" /> Launch This</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Launch (editable before publishing) */}
      <Dialog open={!!launch} onOpenChange={(o) => !o && setLaunch(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tpl-launch-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><RocketLaunch size={17} className="brand-text" weight="fill" /> Launch: {launch?.name}</DialogTitle>
            <DialogDescription className="text-[#7a7a7a] text-xs">Edit anything before launch. The campaign lands in the Approval Queue — nothing publishes automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-caps">Caption</Label>
              <Textarea data-testid="tpl-launch-caption" value={edit.caption} onChange={(e) => setEdit({ ...edit, caption: e.target.value })} rows={4} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
            <div><Label className="label-caps">Hashtags</Label>
              <Input data-testid="tpl-launch-hashtags" value={edit.hashtags} onChange={(e) => setEdit({ ...edit, hashtags: e.target.value })} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
            <div><Label className="label-caps">Call to Action</Label>
              <Input data-testid="tpl-launch-cta" value={edit.cta} onChange={(e) => setEdit({ ...edit, cta: e.target.value })} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
            <label className="flex items-center gap-2 text-xs text-[#cfcfcf] cursor-pointer">
              <Checkbox data-testid="tpl-launch-personalize" checked={edit.personalize} onCheckedChange={(v) => setEdit({ ...edit, personalize: !!v })} />
              <Sparkle size={13} className="brand-text" weight="fill" /> AI-personalize to my brand (uses Brand Assets & Brand Kit)
            </label>
            {launch?.prompt && (
              <div className="text-[10px] text-[#7a7a7a] bg-white/[0.03] border border-white/8 rounded-lg p-2.5">
                <span className="text-[#9a9a9a] font-semibold">Image brief for AI Image Studio:</span> {launch.prompt} — {launch.image_style}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="tpl-launch-confirm" onClick={() => launchM.mutate()} disabled={launchM.isPending}
              className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
              <RocketLaunch size={15} className="mr-1.5" weight="fill" /> {launchM.isPending ? "Launching…" : "Launch to Approval Queue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Custom */}
      <Dialog open={!!custom} onOpenChange={(o) => !o && setCustom(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tpl-custom-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Save as Custom Template</DialogTitle>
            <DialogDescription className="text-[#7a7a7a] text-xs">Duplicate and adjust — optionally share it across all your workspaces.</DialogDescription>
          </DialogHeader>
          {custom && (
            <div className="space-y-3">
              <div><Label className="label-caps">Name</Label>
                <Input data-testid="tpl-custom-name" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Category</Label>
                  <select data-testid="tpl-custom-category" value={custom.category} onChange={(e) => setCustom({ ...custom, category: e.target.value })}
                    className="w-full bg-[#10101A] border border-white/12 rounded-lg px-2 py-2 text-sm mt-1">
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><Label className="label-caps">Channel</Label>
                  <select data-testid="tpl-custom-channel" value={custom.channel} onChange={(e) => setCustom({ ...custom, channel: e.target.value })}
                    className="w-full bg-[#10101A] border border-white/12 rounded-lg px-2 py-2 text-sm mt-1">
                    {["Facebook", "Instagram", "Google", "Email", "SMS"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></div>
              </div>
              <div><Label className="label-caps">Caption</Label>
                <Textarea data-testid="tpl-custom-caption" value={custom.caption} onChange={(e) => setCustom({ ...custom, caption: e.target.value })} rows={3} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
              <div><Label className="label-caps">Hashtags</Label>
                <Input value={custom.hashtags} onChange={(e) => setCustom({ ...custom, hashtags: e.target.value })} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">CTA</Label>
                  <Input value={custom.cta} onChange={(e) => setCustom({ ...custom, cta: e.target.value })} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
                <div><Label className="label-caps">Budget</Label>
                  <Input value={custom.budget} onChange={(e) => setCustom({ ...custom, budget: e.target.value })} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
              </div>
              <div><Label className="label-caps">AI Image Prompt</Label>
                <Textarea value={custom.prompt} onChange={(e) => setCustom({ ...custom, prompt: e.target.value })} rows={2} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
              <div><Label className="label-caps">Suggested Audience</Label>
                <Input value={custom.audience} onChange={(e) => setCustom({ ...custom, audience: e.target.value })} className="bg-[#10101A] border-white/10 mt-1 text-sm" /></div>
              <label className="flex items-center gap-2 text-xs text-[#cfcfcf] cursor-pointer">
                <Checkbox data-testid="tpl-custom-shared" checked={custom.shared} onCheckedChange={(v) => setCustom({ ...custom, shared: !!v })} />
                <GlobeSimple size={13} className="brand-text" /> Share across all my workspaces
              </label>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="tpl-custom-save" onClick={() => saveCustom.mutate()} disabled={saveCustom.isPending}
              className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
